// PROMPT 146: plantillas de WhatsApp desde la plataforma.
// Lista con el estado que dice Meta, editor con vista previa y borrado.
// Crear, editar y borrar es solo del administrador (el servidor lo exige igual).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowLeft,
  Copy,
  FileText,
  Image as ImageIcon,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Send,
  Sparkles,
  Trash2,
  Upload,
  Video,
  X,
} from "lucide-react";
import { colorDeMarca, nombreCorto, tonos } from "@/lib/marcasWa";
import {
  AUTOCOMPLETAR,
  AYUDA_CATEGORIA,
  CATEGORIAS,
  FILTROS_ESTADO,
  IDIOMAS,
  LIMITES,
  NOTA_COSTO,
  aPayload,
  botonVacio,
  calidadInfo,
  categoriaNombre,
  desdePlantilla,
  desdeSugerida,
  estadoInfo,
  haceCuanto,
  idiomaNombre,
  minutosDesde,
  motivoTexto,
  normalizarNombre,
  partesFormato,
  plantillaVacia,
  proximaVariable,
  sincronizarVariables,
  textoConEjemplos,
  validar,
  variablesDe,
} from "@/lib/plantillasWa";

const MINUTOS_SINCRONIZAR = 10;
const CLASE_SELECT =
  "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";
const CLASE_GRIS = "bg-slate-100 text-slate-700 border-slate-200";
const TIPOS_ENCABEZADO = [
  { valor: "", texto: "Sin encabezado" },
  { valor: "TEXT", texto: "Texto" },
  { valor: "IMAGE", texto: "Imagen" },
  { valor: "VIDEO", texto: "Video" },
  { valor: "DOCUMENT", texto: "Documento" },
];
const TIPOS_BOTON = [
  { valor: "QUICK_REPLY", texto: "Respuesta rápida" },
  { valor: "URL", texto: "Enlace (URL)" },
  { valor: "PHONE_NUMBER", texto: "Llamar por teléfono" },
];
const ACEPTA_ARCHIVO = {
  IMAGE: "image/jpeg,image/png",
  VIDEO: "video/mp4",
  DOCUMENT: "application/pdf",
};

const sinAcentos = (t) =>
  String(t || "")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase();

function Chip({ clase, children, ...resto }) {
  return (
    <span
      className={
        "inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium " +
        clase
      }
      {...resto}
    >
      {children}
    </span>
  );
}

function Contador({ n, max }) {
  return (
    <span className={"text-xs " + (n > max ? "text-red-600" : "text-muted-foreground")}>
      {n}/{max}
    </span>
  );
}

// *negrita*, _cursiva_ y ~tachado~ como los muestra WhatsApp
function Formateado({ texto }) {
  return (
    <>
      {partesFormato(texto).map((p, i) => {
        if (p.e === "*") return <strong key={i}>{p.t}</strong>;
        if (p.e === "_") return <em key={i}>{p.t}</em>;
        if (p.e === "~") return <s key={i}>{p.t}</s>;
        return <span key={i}>{p.t}</span>;
      })}
    </>
  );
}

function VistaPrevia({ datos }) {
  const d = sincronizarVariables({
    ...datos,
    botones: datos.botones || [],
    cuerpo: datos.cuerpo || { texto: "" },
  });
  const enc = d.encabezado;
  const media = enc && ["IMAGE", "VIDEO", "DOCUMENT"].includes(enc.tipo);
  const textoEnc =
    enc && enc.tipo === "TEXT" ? textoConEjemplos(enc.texto, d.variables, "encabezado") : "";
  const cuerpo = textoConEjemplos(d.cuerpo.texto, d.variables, "cuerpo");
  const Icono = enc && enc.tipo === "VIDEO" ? Video : enc && enc.tipo === "DOCUMENT" ? FileText : ImageIcon;
  return (
    <div className="rounded-xl bg-[#e7ded3] p-3" data-testid="wa-plantilla-vista-previa">
      <div className="max-w-[320px] rounded-lg rounded-tl-none bg-white p-2 shadow-sm">
        {textoEnc ? (
          <p className="mb-1 break-words text-sm font-semibold text-slate-900">{textoEnc}</p>
        ) : null}
        {media ? (
          <div className="mb-1 flex h-28 flex-col items-center justify-center gap-1 rounded-md bg-slate-200 text-slate-500">
            <Icono className="h-6 w-6" />
            <span className="px-2 text-center text-xs">
              {enc.archivo_id || enc.handle
                ? enc.nombre_archivo || "Archivo de ejemplo cargado"
                : "Falta el archivo de ejemplo"}
            </span>
          </div>
        ) : null}
        <p className="whitespace-pre-wrap break-words text-sm text-slate-900">
          {cuerpo ? <Formateado texto={cuerpo} /> : <span className="text-slate-400">El texto del mensaje…</span>}
        </p>
        {d.pie ? <p className="mt-1 break-words text-xs text-slate-500">{d.pie}</p> : null}
      </div>
      {d.botones.length ? (
        <div className="mt-1 space-y-1">
          {d.botones.map((b, i) => (
            <div
              key={i}
              className="max-w-[320px] rounded-lg bg-white px-2 py-1.5 text-center text-sm font-medium text-sky-700 shadow-sm"
            >
              {b.texto || "Botón"}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function FilaIdioma({ p, esAdmin, onEditar, onCopiar, onBorrar }) {
  const est = estadoInfo(p.estado);
  const cal = calidadInfo(p.calidad);
  const codigo = String(p.estado || "").toUpperCase();
  const motivo = motivoTexto(p.motivo_rechazo);
  return (
    <div className="rounded-lg border bg-card p-3" data-testid={"wa-plantilla-" + p.id}>
      <div className="flex flex-wrap items-center gap-1.5">
        <Chip clase={CLASE_GRIS}>{idiomaNombre(p.idioma)}</Chip>
        <Chip clase={est.clase} data-testid={"wa-plantilla-estado-" + p.id}>
          {est.texto}
        </Chip>
        {p.marcada ? (
          <Chip clase="bg-red-100 text-red-800 border-red-200">Marcada por baja calidad</Chip>
        ) : null}
        {cal ? <Chip clase={cal.clase}>{cal.texto}</Chip> : null}
        <Chip clase={CLASE_GRIS}>{categoriaNombre(p.categoria)}</Chip>
        {p.necesita_sincronizar ? (
          <Chip clase="bg-amber-100 text-amber-900 border-amber-200">Falta confirmar con Meta</Chip>
        ) : null}
        {p.actualizada_en || p.sincronizada_en ? (
          <span className="ml-auto text-xs text-muted-foreground">
            {haceCuanto(p.actualizada_en || p.sincronizada_en)}
          </span>
        ) : null}
      </div>
      {p.texto_cuerpo ? (
        <p className="mt-2 line-clamp-3 whitespace-pre-wrap break-words text-sm text-slate-700">
          {p.texto_cuerpo}
        </p>
      ) : null}
      {p.categoria_anterior && p.categoria_anterior !== p.categoria ? (
        <p className="mt-1 text-xs text-amber-800">
          Meta cambió la categoría: antes era {categoriaNombre(p.categoria_anterior)}.
        </p>
      ) : null}
      {codigo === "REJECTED" ? (
        <div
          className="mt-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-900"
          data-testid={"wa-plantilla-rechazo-" + p.id}
        >
          <p className="font-medium">Meta la rechazó{motivo ? ": " + motivo : ""}.</p>
          {p.detalle_rechazo ? <p className="mt-0.5">{p.detalle_rechazo}</p> : null}
          {esAdmin ? <p className="mt-0.5">Corregila desde Editar y se manda de nuevo a Meta.</p> : null}
        </div>
      ) : null}
      {codigo === "PENDING" || codigo === "IN_APPEAL" ? (
        <p className="mt-2 text-xs text-muted-foreground">Mientras Meta la revisa no se puede editar.</p>
      ) : null}
      {esAdmin ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {p.editable ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => onEditar(p)}
              data-testid={"wa-plantilla-editar-" + p.id}
            >
              <Pencil className="mr-1 h-3.5 w-3.5" />
              Editar
            </Button>
          ) : null}
          {codigo !== "DELETED" && codigo !== "PENDING_DELETION" ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => onCopiar(p)}
              data-testid={"wa-plantilla-copiar-" + p.id}
            >
              <Copy className="mr-1 h-3.5 w-3.5" />
              Copiar a otro idioma
            </Button>
          ) : null}
          {p.borrable ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="text-red-700"
              onClick={() => onBorrar(p)}
              data-testid={"wa-plantilla-borrar-" + p.id}
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" />
              Borrar
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

const rotuloComponente = (c) => {
  if (c === "encabezado") return "encabezado";
  if (c === "cuerpo") return "mensaje";
  const m = /^boton:(\d+)$/.exec(String(c || ""));
  return m ? "enlace del botón " + (Number(m[1]) + 1) : String(c || "");
};

function EditorPlantilla({ inicial, modo, id, idiomasUsados, onEnviada }) {
  const [d, setD] = useState(() => sincronizarVariables(inicial));
  const [subiendo, setSubiendo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [errorServidor, setErrorServidor] = useState("");
  const cuerpoRef = useRef(null);

  const cambiar = (parche) => setD((x) => sincronizarVariables({ ...x, ...parche }));

  const errores = useMemo(() => {
    const lista = validar(d);
    if (modo === "copiar" && d.idioma && idiomasUsados.includes(d.idioma)) {
      lista.push("Ya existe esta plantilla en " + idiomaNombre(d.idioma) + ": elegí otro idioma.");
    }
    return lista;
  }, [d, modo, idiomasUsados]);

  const cambiarVariable = (clave, parche) =>
    setD((x) => ({
      ...x,
      variables: x.variables.map((v) =>
        v.componente + "#" + v.n === clave ? { ...v, ...parche } : v
      ),
    }));

  const elegirAutocompletar = (v, valor) => {
    const nuevo = AUTOCOMPLETAR.find((a) => a.valor === valor);
    const previo = AUTOCOMPLETAR.find((a) => a.valor === v.autocompletar);
    const vacio =
      !String(v.ejemplo || "").trim() || (previo && v.ejemplo === previo.ejemplo);
    cambiarVariable(v.componente + "#" + v.n, {
      autocompletar: valor || null,
      ejemplo: vacio ? (nuevo ? nuevo.ejemplo : "") : v.ejemplo,
    });
  };

  const agregarVariable = () => {
    const texto = d.cuerpo.texto || "";
    const marca = "{{" + proximaVariable(texto) + "}}";
    const el = cuerpoRef.current;
    const ini = el ? el.selectionStart : texto.length;
    const fin = el ? el.selectionEnd : texto.length;
    cambiar({ cuerpo: { texto: texto.slice(0, ini) + marca + texto.slice(fin) } });
    setTimeout(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(ini + marca.length, ini + marca.length);
      }
    }, 0);
  };

  const agregarVariableEncabezado = () => {
    const enc = d.encabezado;
    if (!enc || enc.tipo !== "TEXT" || variablesDe(enc.texto).length) return;
    cambiar({ encabezado: { ...enc, texto: (enc.texto || "") + "{{1}}" } });
  };

  const cambiarTipoEncabezado = (tipo) =>
    cambiar({
      encabezado: tipo ? { tipo, texto: "", archivo_id: null, handle: null } : null,
    });

  const agregarBoton = (tipo) => {
    if (d.botones.length >= LIMITES.botones) return;
    cambiar({ botones: [...d.botones, botonVacio(tipo)] });
  };
  const cambiarBoton = (i, parche) =>
    cambiar({ botones: d.botones.map((b, j) => (j === i ? { ...b, ...parche } : b)) });
  const quitarBoton = (i) => cambiar({ botones: d.botones.filter((_, j) => j !== i) });

  async function subirArchivo(ev) {
    const archivo = ev.target.files && ev.target.files[0];
    ev.target.value = "";
    if (!archivo || !d.encabezado) return;
    setSubiendo(true);
    setErrorServidor("");
    try {
      const fd = new FormData();
      fd.append("archivo", archivo);
      fd.append("tipo", d.encabezado.tipo);
      const { data } = await api.post("/whatsapp/plantillas/archivos", fd);
      setD((x) =>
        sincronizarVariables({
          ...x,
          encabezado: {
            ...x.encabezado,
            archivo_id: data.archivo_id,
            handle: null,
            nombre_archivo: data.nombre || archivo.name,
          },
        })
      );
      toast.success("Archivo de ejemplo cargado.");
    } catch (e) {
      setErrorServidor(
        formatApiError(e.response?.data?.detail) || "No se pudo cargar el archivo."
      );
    } finally {
      setSubiendo(false);
    }
  }

  async function enviar() {
    if (errores.length || enviando) return;
    setEnviando(true);
    setErrorServidor("");
    try {
      const payload = aPayload(d);
      const r =
        modo === "editar"
          ? await api.put("/whatsapp/plantillas/" + id, payload)
          : await api.post("/whatsapp/plantillas", payload);
      toast.success((r.data?.mensaje || "Enviada a Meta para aprobar") + ".");
      onEnviada(r.data);
    } catch (e) {
      setErrorServidor(
        formatApiError(e.response?.data?.detail) || "No se pudo mandar la plantilla a Meta."
      );
    } finally {
      setEnviando(false);
    }
  }

  const enc = d.encabezado;
  const bloqueaNombre = modo === "editar" || modo === "copiar";
  const idiomaConocido = IDIOMAS.some((i) => i.codigo === d.idioma);
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]" data-testid="wa-plantilla-editor">
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="wa-plantilla-nombre">Nombre</Label>
              <Contador n={String(d.nombre || "").length} max={LIMITES.nombre} />
            </div>
            <Input
              id="wa-plantilla-nombre"
              value={d.nombre}
              disabled={bloqueaNombre}
              onChange={(e) => cambiar({ nombre: normalizarNombre(e.target.value) })}
              placeholder="recordatorio_de_tour"
              data-testid="wa-plantilla-nombre"
            />
            <p className="text-xs text-muted-foreground">
              Solo minúsculas, números y guiones bajos.{" "}
              {modo === "editar"
                ? "El nombre y el idioma no se pueden cambiar."
                : modo === "copiar"
                ? "La copia lleva el mismo nombre que la original."
                : "Después no se puede cambiar."}
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="wa-plantilla-idioma">Idioma</Label>
            <select
              id="wa-plantilla-idioma"
              className={CLASE_SELECT}
              value={d.idioma}
              disabled={modo === "editar"}
              onChange={(e) => cambiar({ idioma: e.target.value })}
              data-testid="wa-plantilla-idioma"
            >
              {!idiomaConocido && d.idioma ? <option value={d.idioma}>{d.idioma}</option> : null}
              {IDIOMAS.map((i) => (
                <option
                  key={i.codigo}
                  value={i.codigo}
                  disabled={modo === "copiar" && idiomasUsados.includes(i.codigo)}
                >
                  {i.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="wa-plantilla-categoria">Categoría</Label>
            <select
              id="wa-plantilla-categoria"
              className={CLASE_SELECT}
              value={d.categoria}
              onChange={(e) => cambiar({ categoria: e.target.value })}
              data-testid="wa-plantilla-categoria"
            >
              {CATEGORIAS.map((c) => (
                <option key={c.valor} value={c.valor}>
                  {c.nombre}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-muted-foreground sm:col-span-2">{AYUDA_CATEGORIA}</p>
        </div>

        <div className="space-y-2 rounded-lg border p-3">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="wa-plantilla-enc-tipo">Encabezado (opcional)</Label>
            {enc && enc.tipo === "TEXT" ? (
              <Contador n={String(enc.texto || "").length} max={LIMITES.encabezado} />
            ) : null}
          </div>
          <select
            id="wa-plantilla-enc-tipo"
            className={CLASE_SELECT}
            value={enc ? enc.tipo : ""}
            onChange={(e) => cambiarTipoEncabezado(e.target.value)}
            data-testid="wa-plantilla-enc-tipo"
          >
            {TIPOS_ENCABEZADO.map((t) => (
              <option key={t.valor} value={t.valor}>
                {t.texto}
              </option>
            ))}
          </select>
          {enc && enc.tipo === "TEXT" ? (
            <div className="space-y-1">
              <Input
                value={enc.texto || ""}
                onChange={(e) => cambiar({ encabezado: { ...enc, texto: e.target.value } })}
                placeholder="Título corto"
                data-testid="wa-plantilla-enc-texto"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={agregarVariableEncabezado}
                disabled={variablesDe(enc.texto).length > 0}
                data-testid="wa-plantilla-enc-variable"
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                Agregar variable
              </Button>
            </div>
          ) : null}
          {enc && ["IMAGE", "VIDEO", "DOCUMENT"].includes(enc.tipo) ? (
            <div className="space-y-1">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm">
                {subiendo ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                {enc.archivo_id || enc.handle ? "Cambiar el archivo de ejemplo" : "Subir un archivo de ejemplo"}
                <input
                  type="file"
                  className="hidden"
                  accept={ACEPTA_ARCHIVO[enc.tipo]}
                  disabled={subiendo}
                  onChange={subirArchivo}
                  data-testid="wa-plantilla-enc-archivo"
                />
              </label>
              {enc.nombre_archivo ? (
                <p className="text-xs text-slate-700">{enc.nombre_archivo}</p>
              ) : null}
              <p className="text-xs text-muted-foreground">
                Meta lo usa como ejemplo para aprobar la plantilla. Al mandarla se adjunta el archivo que elijas en ese momento.
              </p>
            </div>
          ) : null}
        </div>

        <div className="space-y-2 rounded-lg border p-3">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="wa-plantilla-cuerpo">Mensaje</Label>
            <Contador n={String(d.cuerpo.texto || "").length} max={LIMITES.cuerpo} />
          </div>
          <Textarea
            id="wa-plantilla-cuerpo"
            ref={cuerpoRef}
            rows={6}
            value={d.cuerpo.texto}
            onChange={(e) => cambiar({ cuerpo: { texto: e.target.value } })}
            placeholder="Hola {{1}}, te recordamos tu tour de mañana."
            data-testid="wa-plantilla-cuerpo"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={agregarVariable}
              data-testid="wa-plantilla-agregar-variable"
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              Agregar variable
            </Button>
            <span className="text-xs text-muted-foreground">
              Negrita *así*, cursiva _así_, tachado ~así~.
            </span>
          </div>
        </div>

        {d.variables.length ? (
          <div className="space-y-2 rounded-lg border p-3" data-testid="wa-plantilla-variables">
            <p className="text-sm font-medium">Variables</p>
            <p className="text-xs text-muted-foreground">
              Meta pide un ejemplo de cada variable para aprobar la plantilla. En “De dónde sale” eligí el dato de la reserva que la va a completar.
            </p>
            {d.variables.map((v) => {
              const clave = v.componente + "#" + v.n;
              return (
                <div
                  key={clave}
                  className="grid gap-2 rounded-md bg-slate-50 p-2 sm:grid-cols-3"
                  data-testid={"wa-plantilla-variable-" + v.componente.replace(":", "-") + "-" + v.n}
                >
                  <p className="text-xs font-medium sm:col-span-3">
                    {"{{" + v.n + "}}"} · {rotuloComponente(v.componente)}
                  </p>
                  <Input
                    value={v.etiqueta || ""}
                    onChange={(e) => cambiarVariable(clave, { etiqueta: e.target.value })}
                    placeholder="Qué es (ej. nombre)"
                    aria-label={"Nombre de la variable " + v.n}
                  />
                  <Input
                    value={v.ejemplo || ""}
                    onChange={(e) => cambiarVariable(clave, { ejemplo: e.target.value })}
                    placeholder="Ejemplo (ej. Ana)"
                    aria-label={"Ejemplo de la variable " + v.n}
                  />
                  <select
                    className={CLASE_SELECT}
                    value={v.autocompletar || ""}
                    onChange={(e) => elegirAutocompletar(v, e.target.value)}
                    aria-label={"De dónde sale la variable " + v.n}
                  >
                    <option value="">De dónde sale: se escribe a mano</option>
                    {AUTOCOMPLETAR.map((a) => (
                      <option key={a.valor} value={a.valor}>
                        {a.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        ) : null}

        <div className="space-y-1 rounded-lg border p-3">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="wa-plantilla-pie">Pie (opcional)</Label>
            <Contador n={String(d.pie || "").length} max={LIMITES.pie} />
          </div>
          <Input
            id="wa-plantilla-pie"
            value={d.pie || ""}
            onChange={(e) => cambiar({ pie: e.target.value })}
            placeholder="Tours NY"
            data-testid="wa-plantilla-pie"
          />
        </div>

        <div className="space-y-2 rounded-lg border p-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium">Botones (opcional)</p>
            <Contador n={d.botones.length} max={LIMITES.botones} />
          </div>
          {d.botones.map((b, i) => (
            <div key={i} className="space-y-2 rounded-md bg-slate-50 p-2" data-testid={"wa-plantilla-boton-" + i}>
              <div className="flex items-center gap-2">
                <select
                  className={CLASE_SELECT}
                  value={b.tipo}
                  onChange={(e) => cambiarBoton(i, { tipo: e.target.value })}
                  aria-label={"Tipo del botón " + (i + 1)}
                >
                  {TIPOS_BOTON.map((t) => (
                    <option key={t.valor} value={t.valor}>
                      {t.texto}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => quitarBoton(i)}
                  aria-label={"Quitar el botón " + (i + 1)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  value={b.texto || ""}
                  onChange={(e) => cambiarBoton(i, { texto: e.target.value })}
                  placeholder="Texto del botón"
                  aria-label={"Texto del botón " + (i + 1)}
                />
                <Contador n={String(b.texto || "").length} max={LIMITES.boton} />
              </div>
              {b.tipo === "URL" ? (
                <Input
                  value={b.url || ""}
                  onChange={(e) => cambiarBoton(i, { url: e.target.value })}
                  placeholder="https://ejemplo.com/reserva/{{1}}"
                  aria-label={"Dirección del botón " + (i + 1)}
                />
              ) : null}
              {b.tipo === "PHONE_NUMBER" ? (
                <Input
                  value={b.telefono || ""}
                  onChange={(e) => cambiarBoton(i, { telefono: e.target.value })}
                  placeholder="+12012565752"
                  aria-label={"Teléfono del botón " + (i + 1)}
                />
              ) : null}
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            {TIPOS_BOTON.map((t) => (
              <Button
                key={t.valor}
                type="button"
                size="sm"
                variant="outline"
                disabled={d.botones.length >= LIMITES.botones}
                onClick={() => agregarBoton(t.valor)}
                data-testid={"wa-plantilla-agregar-boton-" + t.valor.toLowerCase()}
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                {t.texto}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-3 lg:sticky lg:top-0 lg:self-start">
        <p className="text-sm font-medium">Así se va a ver</p>
        <VistaPrevia datos={d} />
        {errores.length ? (
          <div
            className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900"
            data-testid="wa-plantilla-errores"
          >
            <p className="mb-1 flex items-center gap-1 font-medium">
              <AlertTriangle className="h-3.5 w-3.5" />
              Falta resolver:
            </p>
            <ul className="list-disc space-y-0.5 pl-4">
              {errores.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {errorServidor ? (
          <div
            className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-900"
            data-testid="wa-plantilla-error-servidor"
          >
            {errorServidor}
          </div>
        ) : null}
        <p className="text-xs text-muted-foreground">{NOTA_COSTO}</p>
        {modo === "editar" ? (
          <p className="text-xs text-muted-foreground">Al editarla, Meta la revisa de nuevo.</p>
        ) : null}
        <Button
          type="button"
          className="w-full"
          onClick={enviar}
          disabled={enviando || subiendo || errores.length > 0}
          data-testid="wa-plantilla-enviar"
        >
          {enviando ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Send className="mr-2 h-4 w-4" />
          )}
          Enviar a Meta para aprobar
        </Button>
      </div>
    </div>
  );
}

const CLASE_SELECT_CHICO =
  "h-9 rounded-md border border-input bg-background px-2 text-sm sm:w-44";

export default function PlantillasDialog({ open, onOpenChange }) {
  const { isAdmin } = useAuth();
  const [canalListo, setCanalListo] = useState(false);
  const [cuentas, setCuentas] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [activa, setActiva] = useState(null);
  const [plantillas, setPlantillas] = useState([]);
  const [sincronizadaEn, setSincronizadaEn] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [error, setError] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [filtro, setFiltro] = useState("TODAS");
  const [vista, setVista] = useState("lista");
  const [editor, setEditor] = useState(null);
  const [sugeridas, setSugeridas] = useState(null);
  const [cargandoSug, setCargandoSug] = useState(false);
  const [aBorrar, setABorrar] = useState(null);
  const [borrando, setBorrando] = useState(false);
  const yaSincronizadas = useRef(new Set());
  const cuenta = cuentas.find((c) => c.waba === activa) || null;

  const cargarPlantillas = useCallback(async (c, estado) => {
    if (!c) return null;
    setCargando(true);
    setError("");
    try {
      const params = { marca_slug: c.slug };
      if (estado === "DELETED") params.estado = "DELETED";
      const { data } = await api.get("/whatsapp/plantillas", { params });
      setPlantillas(data?.plantillas || []);
      setSincronizadaEn(data?.sincronizada_en || null);
      return data;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "No se pudieron leer las plantillas.");
      return null;
    } finally {
      setCargando(false);
    }
  }, []);

  // Lee de Meta (solo lectura) y actualiza la copia que se muestra.
  const sincronizar = useCallback(
    async (c, estado, silencioso) => {
      if (!c) return;
      setSincronizando(true);
      try {
        const { data } = await api.post("/whatsapp/plantillas/sincronizar", {
          marca_slug: c.slug,
        });
        const r = (data?.cuentas || [])[0] || null;
        await cargarPlantillas(c, estado);
        if (r && r.ok === false) {
          setError(r.error || "Meta no respondió. Probá de nuevo en unos minutos.");
        } else if (!silencioso && r) {
          toast.success(
            "Sincronizado con Meta: " + r.nuevas + " nuevas, " + r.actualizadas + " actualizadas, " + r.borradas + " borradas."
          );
        }
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || "No se pudo sincronizar con Meta.");
      } finally {
        setSincronizando(false);
      }
    },
    [cargarPlantillas]
  );

  useEffect(() => {
    if (!open) {
      setVista("lista");
      setEditor(null);
      setABorrar(null);
      setBusqueda("");
      setFiltro("TODAS");
      setCanalListo(false);
      yaSincronizadas.current = new Set();
      return undefined;
    }
    let vivo = true;
    (async () => {
      try {
        const { data } = await api.get("/whatsapp/canal");
        if (!vivo) return;
        const lista = data?.marcas || [];
        const porWaba = new Map();
        lista.forEach((m) => {
          if (!m.wa_waba_id) return;
          if (!porWaba.has(m.wa_waba_id)) porWaba.set(m.wa_waba_id, []);
          porWaba.get(m.wa_waba_id).push(m);
        });
        const cs = Array.from(porWaba, ([waba, ms]) => ({ waba, marcas: ms, slug: ms[0].slug }));
        setMarcas(lista);
        setCuentas(cs);
        setActiva(cs.length ? cs[0].waba : null);
      } catch (e) {
        if (vivo) {
          setError(formatApiError(e.response?.data?.detail) || "No se pudo leer el canal de WhatsApp.");
        }
      } finally {
        if (vivo) setCanalListo(true);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [open]);

  // Al abrir una cuenta: se muestra lo guardado y, si hace más de 10 minutos que
  // no se lee de Meta (o nunca), se sincroniza una sola vez por apertura.
  useEffect(() => {
    if (!open || !cuenta) return undefined;
    let vivo = true;
    (async () => {
      const data = await cargarPlantillas(cuenta, filtro);
      if (!vivo || !data) return;
      if (!yaSincronizadas.current.has(cuenta.waba)) {
        yaSincronizadas.current.add(cuenta.waba);
        if (minutosDesde(data.sincronizada_en) > MINUTOS_SINCRONIZAR) {
          await sincronizar(cuenta, filtro, true);
        }
      }
    })();
    return () => {
      vivo = false;
    };
  }, [open, cuenta, filtro, cargarPlantillas, sincronizar]);

  const grupos = useMemo(() => {
    const q = sinAcentos(busqueda.trim());
    const mapa = new Map();
    plantillas
      .filter((p) => {
        const est = String(p.estado || "").toUpperCase();
        if (filtro === "PENDING" && est !== "PENDING" && est !== "IN_APPEAL") return false;
        if (["APPROVED", "REJECTED", "PAUSED"].includes(filtro) && est !== filtro) return false;
        if (!q) return true;
        return sinAcentos([p.nombre, p.texto_cuerpo, idiomaNombre(p.idioma)].join(" ")).includes(q);
      })
      .forEach((p) => {
        if (!mapa.has(p.nombre)) mapa.set(p.nombre, []);
        mapa.get(p.nombre).push(p);
      });
    return Array.from(mapa, ([nombre, items]) => ({ nombre, items }));
  }, [plantillas, busqueda, filtro]);

  const abrirEditor = (datos) => {
    setEditor({ ...datos, clave: Date.now() });
    setVista("editor");
  };
  const volverALista = () => {
    setVista("lista");
    setEditor(null);
  };
  const abrirNueva = () => {
    if (!cuenta) return;
    abrirEditor({ modo: "nueva", id: null, inicial: plantillaVacia(cuenta.slug), idiomasUsados: [] });
  };
  const abrirEditar = (p) => {
    abrirEditor({ modo: "editar", id: p.id, inicial: desdePlantilla(p, cuenta.slug), idiomasUsados: [] });
  };
  const abrirCopia = (p) => {
    const usados = plantillas
      .filter((x) => x.nombre === p.nombre && String(x.estado || "").toUpperCase() !== "DELETED")
      .map((x) => x.idioma);
    const libre = IDIOMAS.find((i) => !usados.includes(i.codigo));
    abrirEditor({
      modo: "copiar",
      id: null,
      inicial: { ...desdePlantilla(p, cuenta.slug), idioma: libre ? libre.codigo : "" },
      idiomasUsados: usados,
    });
  };
  const abrirSugeridas = async () => {
    setVista("sugeridas");
    if (sugeridas || cargandoSug) return;
    setCargandoSug(true);
    try {
      const { data } = await api.get("/whatsapp/plantillas/sugeridas");
      setSugeridas(data?.sugeridas || []);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "No se pudieron leer las plantillas sugeridas.");
      setSugeridas([]);
    } finally {
      setCargandoSug(false);
    }
  };
  const usarSugerida = (s, idioma) => {
    if (!cuenta) return;
    abrirEditor({ modo: "nueva", id: null, inicial: desdeSugerida(s, idioma, cuenta.slug), idiomasUsados: [] });
  };
  const alEnviar = async () => {
    volverALista();
    if (cuenta) await cargarPlantillas(cuenta, filtro);
  };
  const cambiarCuenta = (waba) => {
    setActiva(waba);
    setBusqueda("");
    setPlantillas([]);
    setSincronizadaEn(null);
    setABorrar(null);
  };

  const cantidadBorrable = aBorrar
    ? plantillas.filter((x) => x.nombre === aBorrar.nombre && x.borrable).length
    : 0;
  const confirmarBorrado = async (todos) => {
    if (!aBorrar) return;
    setBorrando(true);
    try {
      const { data } = await api.delete("/whatsapp/plantillas/" + aBorrar.id, {
        params: { todos_los_idiomas: todos },
      });
      toast.success(data?.mensaje || "Plantilla borrada.");
      setABorrar(null);
      if (cuenta) await cargarPlantillas(cuenta, filtro);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo borrar la plantilla.");
    } finally {
      setBorrando(false);
    }
  };

  const titulo =
    vista === "editor"
      ? editor && editor.modo === "editar"
        ? "Editar plantilla"
        : editor && editor.modo === "copiar"
        ? "Copiar a otro idioma"
        : "Nueva plantilla"
      : vista === "sugeridas"
      ? "Plantillas sugeridas"
      : "Plantillas de WhatsApp";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto" data-testid="wa-plantillas-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex flex-wrap items-center gap-2 pr-6">
            {vista !== "lista" ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={volverALista}
                data-testid="wa-plantillas-volver"
              >
                <ArrowLeft className="mr-1 h-4 w-4" />
                Volver
              </Button>
            ) : null}
            <FileText className="h-5 w-5" />
            {titulo}
          </DialogTitle>
          <DialogDescription>
            Son los mensajes que se pueden mandar fuera de la ventana de 24 horas. Meta los tiene que aprobar antes de usarlos.
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-900"
            data-testid="wa-plantillas-error"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="flex-1 break-words">{error}</span>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-6 w-6"
              onClick={() => setError("")}
              aria-label="Cerrar el aviso"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : null}

        {vista === "editor" && editor ? (
          <EditorPlantilla
            key={editor.clave}
            inicial={editor.inicial}
            modo={editor.modo}
            id={editor.id}
            idiomasUsados={editor.idiomasUsados}
            onEnviada={alEnviar}
          />
        ) : null}

        {vista === "sugeridas" ? (
          <div className="space-y-3" data-testid="wa-plantillas-sugeridas">
            {cargandoSug ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Leyendo las sugeridas…
              </p>
            ) : null}
            {(sugeridas || []).map((s) => (
              <div
                key={s.clave}
                className="space-y-1 rounded-lg border p-3"
                data-testid={"wa-sugerida-" + s.clave}
              >
                <p className="text-sm font-medium">{s.titulo}</p>
                <p className="text-sm text-slate-700">{s.descripcion}</p>
                {s.aviso ? <p className="text-xs text-amber-800">{s.aviso}</p> : null}
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => usarSugerida(s, "es")}
                    data-testid={"wa-sugerida-es-" + s.clave}
                  >
                    <Sparkles className="mr-1 h-3.5 w-3.5" />
                    Usar en español
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => usarSugerida(s, "en")}
                    data-testid={"wa-sugerida-en-" + s.clave}
                  >
                    <Sparkles className="mr-1 h-3.5 w-3.5" />
                    Usar en inglés
                  </Button>
                </div>
              </div>
            ))}
            {sugeridas && !sugeridas.length && !cargandoSug ? (
              <p className="text-sm text-muted-foreground">No hay plantillas sugeridas por ahora.</p>
            ) : null}
          </div>
        ) : null}

        {vista === "lista" ? (
          <div className="space-y-3">
            {cuentas.length ? (
              <div className="flex flex-wrap gap-2" role="tablist" data-testid="wa-plantillas-cuentas">
                {cuentas.map((c) => {
                  const t = tonos(colorDeMarca(c.slug, marcas));
                  const sel = c.waba === activa;
                  return (
                    <button
                      key={c.waba}
                      type="button"
                      role="tab"
                      aria-selected={sel}
                      onClick={() => cambiarCuenta(c.waba)}
                      className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium"
                      style={
                        sel
                          ? { backgroundColor: t.suave, borderColor: t.fuerte, color: t.texto }
                          : { borderColor: t.borde }
                      }
                      data-testid={"wa-plantillas-cuenta-" + c.waba}
                    >
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: t.fuerte }} />
                      {c.marcas.map((m) => nombreCorto(m.slug, marcas)).join(" · ")}
                    </button>
                  );
                })}
              </div>
            ) : null}

            {canalListo && !cuentas.length ? (
              <p className="text-sm text-muted-foreground" data-testid="wa-plantillas-sin-cuenta">
                Ninguna marca tiene cargada la cuenta de WhatsApp Business. Un administrador puede completarla en Canal.
              </p>
            ) : null}

            {cuenta ? (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative min-w-[180px] flex-1">
                    <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      className="pl-8"
                      value={busqueda}
                      onChange={(e) => setBusqueda(e.target.value)}
                      placeholder="Buscar por nombre o texto"
                      data-testid="wa-plantillas-buscar"
                    />
                  </div>
                  <select
                    className={CLASE_SELECT_CHICO}
                    value={filtro}
                    onChange={(e) => setFiltro(e.target.value)}
                    aria-label="Filtrar por estado"
                    data-testid="wa-plantillas-filtro"
                  >
                    {FILTROS_ESTADO.map((f) => (
                      <option key={f.valor} value={f.valor}>
                        {f.texto}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => sincronizar(cuenta, filtro, false)}
                    disabled={sincronizando}
                    data-testid="wa-plantillas-sincronizar"
                  >
                    {sincronizando ? (
                      <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="mr-1 h-4 w-4" />
                    )}
                    Sincronizar con Meta
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground" data-testid="wa-plantillas-sincronizada">
                  Última sincronización: {haceCuanto(sincronizadaEn)}. Sincronizar solo lee de Meta, no cambia nada allá.
                </p>

                {isAdmin ? (
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" size="sm" onClick={abrirNueva} data-testid="wa-plantilla-nueva">
                      <Plus className="mr-1 h-4 w-4" />
                      Nueva plantilla
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={abrirSugeridas}
                      data-testid="wa-plantilla-sugeridas"
                    >
                      <Sparkles className="mr-1 h-4 w-4" />
                      Usar una sugerida
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground" data-testid="wa-plantillas-solo-admin">
                    Solo un administrador puede crear, editar o borrar plantillas.
                  </p>
                )}
              </>
            ) : null}

            {aBorrar ? (
              <div
                className="sticky top-0 z-10 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
                data-testid="wa-plantilla-borrar-confirmar"
              >
                <p className="font-medium">
                  ¿Borrar “{aBorrar.nombre}” en {idiomaNombre(aBorrar.idioma)}?
                </p>
                <p className="mt-1 text-xs">
                  Meta la da de baja y ese nombre en ese idioma no se puede volver a usar durante 30 días.
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    disabled={borrando}
                    onClick={() => confirmarBorrado(false)}
                    data-testid="wa-plantilla-borrar-uno"
                  >
                    {borrando ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                    Borrar solo este idioma
                  </Button>
                  {cantidadBorrable > 1 ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      disabled={borrando}
                      onClick={() => confirmarBorrado(true)}
                      data-testid="wa-plantilla-borrar-todos"
                    >
                      Borrar los {cantidadBorrable} idiomas
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={borrando}
                    onClick={() => setABorrar(null)}
                    data-testid="wa-plantilla-borrar-cancelar"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : null}

            {cargando && !plantillas.length ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Leyendo las plantillas…
              </p>
            ) : null}
            {cuenta && !cargando && !grupos.length ? (
              <p className="text-sm text-muted-foreground" data-testid="wa-plantillas-vacio">
                {plantillas.length
                  ? "Ninguna plantilla coincide con la búsqueda."
                  : "Todavía no hay plantillas en esta cuenta."}
              </p>
            ) : null}
            <div className="space-y-3" data-testid="wa-plantillas-lista">
              {grupos.map((g) => (
                <div
                  key={g.nombre}
                  className="space-y-2 rounded-xl border bg-slate-50/60 p-3"
                  data-testid={"wa-plantilla-grupo-" + g.nombre}
                >
                  <p className="break-all font-mono text-sm font-semibold">{g.nombre}</p>
                  <div className="space-y-2">
                    {g.items.map((p) => (
                      <FilaIdioma
                        key={p.id}
                        p={p}
                        esAdmin={isAdmin}
                        onEditar={abrirEditar}
                        onCopiar={abrirCopia}
                        onBorrar={setABorrar}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
